import type {UseFormReturn} from "react-hook-form";

import {GenderType, TARGET_TYPE_LABELS, TargetType} from "@mytour/shared";
import {Field2} from "@mytour/ui/form";
import {Splitter} from "@mytour/ui/layout";
import z from "zod";

export const serviceTargetSchema = z.object({
  label: z.string().optional(),
  type: z.nativeEnum(TargetType).optional(),
  gender: z.nativeEnum(GenderType).optional(),
  race: z.string().optional(),
  robe: z.string().optional(),
  height: z.number().optional().nullable(),
  weight: z.number().optional().nullable(),
  birthDate: z.string().optional(),
});
export type ServiceTargetForm = z.infer<typeof serviceTargetSchema>;

const ALL_TARGET_TYPES = [TargetType.HORSE, TargetType.DOG, TargetType.CAT, TargetType.HUMAN];

type Props = {
  hookForm: UseFormReturn<ServiceTargetForm>;
  disabled: boolean;
  /** Restrict which target types are shown. When omitted, all types are shown. */
  allowedTargetTypes?: TargetType[];
  isEquine?: boolean;
};

export function StepServiceTarget({
  hookForm,
  disabled,
  allowedTargetTypes,
  isEquine = false,
}: Props) {
  const {watch} = hookForm;
  const targetTypes = allowedTargetTypes ?? ALL_TARGET_TYPES;

  const type = watch("type");
  return (
    <>
      <Field2 name="label" label="Nom" hookForm={hookForm} disabled={disabled} />
      <Splitter>
        {!isEquine && (
          <Field2 name="type" label="Type" type="select" hookForm={hookForm} disabled={disabled}>
            <option value="">Sélectionner</option>
            {targetTypes.map((tt) => (
              <option key={tt} value={tt}>
                {TARGET_TYPE_LABELS[tt]}
              </option>
            ))}
          </Field2>
        )}
        <Field2 name="gender" label="Sexe" type="select" hookForm={hookForm} disabled={disabled}>
          <option value="">Sélectionner</option>
          {type === "horse" ? (
            <>
              <option value="female">Jument</option>
              <option value="gelding">Hongre</option>
              <option value="male">Entier</option>
            </>
          ) : type === "human" ? (
            <>
              <option value="female">Féminin</option>
              <option value="male">Masculin</option>
            </>
          ) : (
            <>
              <option value="female">Femelle</option>
              <option value="male">Mâle</option>
            </>
          )}
        </Field2>
      </Splitter>
      {type !== "human" && (
        <Splitter>
          <Field2 name="race" hookForm={hookForm} disabled={disabled} />
          <Field2 name="robe" hookForm={hookForm} disabled={disabled} />
        </Splitter>
      )}
      <Splitter>
        <Field2
          name="height"
          label="Taille (cm)"
          type="number"
          min={1}
          hookForm={hookForm}
          disabled={disabled}
        />
        <Field2
          name="weight"
          label="Poids (kg)"
          type="number"
          min={1}
          hookForm={hookForm}
          disabled={disabled}
        />
      </Splitter>

      <Field2
        name="birthDate"
        label="Date de naissance"
        type="date"
        hookForm={hookForm}
        disabled={disabled}
      />
    </>
  );
}
